import type {Entry, Locale} from 'contentful'
import _merge from 'lodash.merge'

import getLocales from './getLocales'
import {EntryLocaleMap, Json, LandingPage, Layout, Translation} from './types'

export const normalizeSlashes = (slug: string): string => {
  if (!slug) return ''

  const startingSlash = slug.startsWith('/')
  const endingSlash = slug.endsWith('/')

  if (startingSlash) {
    slug = slug.substring(1)
  }
  if (endingSlash) {
    slug = slug.substring(0, slug.length - 1)
  }

  return startingSlash || endingSlash ? normalizeSlashes(slug) : slug
}

export const normalizeLocalePrefix = (
  slug: string,
  locale: string,
  defaultLocale: string,
): string => {
  let cleanSlug = normalizeSlashes(slug)

  if (locale === defaultLocale) {
    return cleanSlug
  }

  const baseLocale = getBaseLocale(locale)

  if (cleanSlug.indexOf(`${baseLocale}/`) === -1) {
    cleanSlug = `${baseLocale}/${cleanSlug}`
  }

  return cleanSlug
}

export const getBaseLocale = (locale: string): string => locale.split('-')[0]

export const replaceStaticPath = (
  value: string,
  values: Record<string, unknown>,
  name: string,
): void => {
  const srcArr = value
    .trim()
    .split('/')
    .filter(v => !!v)

  if (srcArr[0] === 'static') {
    const assetPrefix = normalizeSlashes(process.env.NEXT_PUBLIC_ASSET_PREFIX || '')
    const path = normalizeSlashes([assetPrefix, 'assets', ...srcArr.slice(1)].join('/'))
    values[name] = path.indexOf('http') !== 0 ? `/${path}` : path
  }
}

export const replaceTextTokens = (value: string, textTokens: Record<string, string>): string => {
  const regex = RegExp('\\[.*?\\]', 'gm')
  const parts = value.match(regex)

  parts?.forEach(token => {
    // slice to remove `[]`
    const tokenKey = token.slice(1, -1)
    const tokenValue = textTokens[tokenKey]

    if (tokenValue) {
      value = value.replaceAll(token, textTokens[tokenKey])
    }
  })

  return value
}

export const replaceSectionValues = (
  values: Record<string, unknown>,
  textTokens: Record<string, string>,
): void => {
  if (values && typeof values === 'object') {
    if (Array.isArray(values)) {
      values.forEach(value => replaceSectionValues(value, textTokens))
      return
    }

    for (const [name, value] of Object.entries(values)) {
      if (typeof value === 'string') {
        // apply text replace here
        values[name] = replaceTextTokens(value, textTokens)

        if (name === 'src') {
          replaceStaticPath(value, values, name)
        }
      }

      if (typeof value === 'object') {
        replaceSectionValues(value as Record<string, unknown>, textTokens)
      }
    }
  }
}

export const getLocaleFromPath = async (slug: string): Promise<Locale> => {
  const slugArr = normalizeSlashes(slug).split('/')

  const allLocales = await getLocales()

  const defaultLocale = allLocales.find(l => l.default)

  return (
    allLocales.find(code => !code.default && slugArr[0] === getBaseLocale(code.code)) ||
    defaultLocale
  )
}

export const isEntryLocalized = (
  defaultEntry: Entry<LandingPage>,
  entry: Entry<LandingPage>,
  defaultLocale: string,
): boolean => {
  if (entry.sys.locale === defaultLocale) {
    return true
  }

  const {siteUrl: defaultSlug} = defaultEntry.fields

  const {siteUrl: entrySlug} = entry.fields

  return defaultSlug !== entrySlug
}

export const normalizeCanonicalUrl = (
  entry: Entry<LandingPage>,
  defaultEntry?: Entry<LandingPage>,
): string | null => {
  let entryCanonicalUrl = entry.fields.canonicalUrl ? toAbsoluteUrl(entry.fields.canonicalUrl) : ''

  const defaultCanonicalUrl = defaultEntry?.fields.canonicalUrl
    ? toAbsoluteUrl(defaultEntry.fields.canonicalUrl)
    : ''

  if (!entryCanonicalUrl || entryCanonicalUrl === defaultCanonicalUrl) {
    entryCanonicalUrl = toAbsoluteUrl(entry.fields.siteUrl)
  }

  return entryCanonicalUrl
}

export const toAbsoluteUrl = (path: string): string => {
  const normalizedPath = normalizeSlashes(path)

  if (!path.startsWith('http')) {
    return [
      process.env.NEXT_PUBLIC_PROTO || 'https://',
      process.env.NEXT_PUBLIC_HOST || 'miro.com',
      normalizedPath.length > 0 ? `/${normalizedPath}/` : '/',
    ].join('')
  }

  return `${normalizedPath}/`
}

export const parseTranslationsMap = (
  entryLocalesMap: EntryLocaleMap<LandingPage>,
  defaultLocaleCode: string,
): Translation[] =>
  Object.entries(entryLocalesMap).reduce((translations, [locale, entry]) => {
    const allowedLocales = entry.fields.allowedLocales || {}

    // skip explicitly disallowed pages
    if (allowedLocales[locale] === false) {
      return translations
    }

    translations.push({
      locale: getBaseLocale(locale),
      isDefault: locale === defaultLocaleCode,
      canonical: entry.fields.canonicalUrl || '',
      url: entry.fields.siteUrl,
    })

    return translations
  }, [])

interface ParseSectionsAttributes {
  sections: Layout
  textTokens: Record<string, string>
  experiments: Record<string, string>
  layoutCustomValues?: Record<string, Json>
}

export const parseSections = ({
  sections,
  textTokens,
  experiments = {},
  layoutCustomValues = {},
}: ParseSectionsAttributes): Layout => {
  /**
   * Formats each section values
   */
  return sections.reduce<Layout>((variants, section, i) => {
    let variant = section

    /**
     * Gets all custom values stored for this section
     */
    const sectionLayoutCustomValues = layoutCustomValues[section.id] || {}

    /**
     * Selects correct variant to display if this is an experiment section
     */
    if (section.isExperimentSection) {
      const treatmentValue = experiments[section.experimentName] || section.defaultVariant
      variant = section.variants[treatmentValue]

      /**
       * Grabs the custom values for this specific treatment and merge
       * them to the selected variant values
       */
      const customVariantValues = sectionLayoutCustomValues.variants ?? {}
      variant.values = _merge({}, variant.values, customVariantValues[treatmentValue])
    } else {
      variant.values = _merge({}, variant.values, sectionLayoutCustomValues)
    }

    if (variant) {
      /**
       * Replace the text tokens inside each section
       * Fixes all images static paths
       */
      replaceSectionValues(variant.values, textTokens)
      variants.push(variant)
    }

    return variants
  }, [])
}
